import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const STOREFRONT_API_VERSION = '2025-01';

/**
 * Right caret for the size chart's horizontal-scroll control.
 *
 * This render path builds HTML as strings, so it cannot use Liquid's
 * `inline_asset_content`. The markup is kept byte-identical to
 * assets/icon-caret-right.svg (the CaretRight glyph exported from the size
 * guide Figma frame) - update both together.
 */
const CARET_RIGHT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">' +
  '<path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

const CARET_LEFT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">' +
  '<path d="M12.5 3.75L6.25 10L12.5 16.25" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

/**
 * Read lazily rather than at module evaluation: `Theme` is defined by an
 * inline script in snippets/scripts.liquid, and the rest of the theme reads
 * it at call time for the same reason.
 * @returns {string}
 */
function scrollLabel() {
  return Theme?.translations?.size_guide_scroll_chart ?? '';
}

/** How far one press of the chart's scroll control moves it, in pixels. */
const CHART_SCROLL_STEP = 224;

/**
 * @typedef {object} SizeGuideConfig
 * @property {string} token
 * @property {string} storeDomain
 * @property {string} metaobjectType
 * @property {string} parentCodeField
 */

/**
 * @typedef {object} MetaobjectField
 * @property {string} key
 * @property {string} [value]
 * @property {string} [type]
 * @property {object} [reference]
 */

/**
 * @returns {SizeGuideConfig | null}
 */
function getStorefrontConfig() {
  const el = document.getElementById('size-guide-storefront-config');
  if (!(el instanceof HTMLScriptElement) || !el.textContent) return null;

  try {
    const config = JSON.parse(el.textContent);
    if (!config?.token || !config?.storeDomain) return null;
    return config;
  } catch {
    return null;
  }
}

/**
 * @param {MetaobjectField[]} fields
 * @param {string} key
 */
function getField(fields, key) {
  return fields.find((field) => field.key === key);
}

/**
 * @param {MetaobjectField[]} fields
 * @param {string} key
 */
function getFieldValue(fields, key) {
  return getField(fields, key)?.value ?? '';
}

/**
 * @param {object} reference
 * @returns {{ url: string, isVideo: boolean } | null}
 */
function getMediaFromReference(reference) {
  if (!reference || typeof reference !== 'object') return null;

  if (reference.image?.url) {
    return { url: reference.image.url, isVideo: false };
  }

  const videoSource = reference.sources?.find((/** @type {{ url?: string }} */ source) => source.url);
  if (videoSource?.url) {
    return { url: videoSource.url, isVideo: true };
  }

  if (reference.url) {
    return { url: reference.url, isVideo: false };
  }

  return null;
}

/** @type {Map<string, MetaobjectField[]> | null} */
let sizeGuideIndex = null;

/** @type {Promise<Map<string, MetaobjectField[]>> | null} */
let sizeGuideIndexPromise = null;

/**
 * Storefront API metaobjects only support type + pagination — no field filters.
 * @param {SizeGuideConfig} config
 * @param {number} first
 * @param {string | null} [after]
 */
async function queryMetaobjects(config, first, after = null) {
  const graphql = `
    query SizeGuides($type: String!, $first: Int!, $after: String) {
      metaobjects(type: $type, first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          fields {
            key
            value
            type
            reference {
              __typename
              ... on MediaImage {
                image {
                  url
                }
              }
              ... on Video {
                sources {
                  url
                  mimeType
                }
              }
              ... on GenericFile {
                url
              }
            }
          }
        }
      }
    }
  `;

  const response = await fetch(`https://${config.storeDomain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': config.token,
    },
    body: JSON.stringify({
      query: graphql,
      variables: {
        type: config.metaobjectType,
        first,
        after,
      },
    }),
  });

  if (!response.ok) return null;

  const payload = await response.json();
  if (payload.errors?.length) {
    console.warn('Size guide Storefront API error:', payload.errors);
    return null;
  }

  return payload.data?.metaobjects ?? null;
}

/**
 * @param {SizeGuideConfig} config
 */
async function buildSizeGuideIndex(config) {
  /** @type {Map<string, MetaobjectField[]>} */
  const index = new Map();
  let after = null;

  do {
    const page = await queryMetaobjects(config, 250, after);
    if (!page) break;

    for (const node of page.nodes ?? []) {
      const parentCode = getFieldValue(node.fields, config.parentCodeField).trim();
      if (parentCode) {
        index.set(parentCode, node.fields);
      }
    }

    after = page.pageInfo?.hasNextPage ? page.pageInfo.endCursor : null;
  } while (after);

  return index;
}

/**
 * @param {SizeGuideConfig} config
 */
async function getSizeGuideIndex(config) {
  if (sizeGuideIndex) return sizeGuideIndex;

  if (!sizeGuideIndexPromise) {
    sizeGuideIndexPromise = buildSizeGuideIndex(config);
  }

  sizeGuideIndex = await sizeGuideIndexPromise;
  return sizeGuideIndex;
}

/**
 * @param {SizeGuideConfig} config
 * @param {string} parentCode
 */
async function fetchSizeGuideByParentCode(config, parentCode) {
  const index = await getSizeGuideIndex(config);
  return index.get(parentCode.trim()) ?? null;
}

/**
 * @param {unknown} tableData
 */
function renderMeasurementTable(tableData) {
  if (!tableData || typeof tableData !== 'object') return '';

  const data = /** @type {{ headers?: string[], columns?: string[], rows?: unknown[][] }} */ (tableData);
  const headers = data.headers ?? data.columns;
  const rows = data.rows;

  if (!Array.isArray(headers) || !Array.isArray(rows) || headers.length === 0 || rows.length === 0) {
    return '';
  }

  const headHtml = headers.map((header) => `<th scope="col">${header}</th>`).join('');
  const bodyHtml = rows
    .map((row) => {
      const cells = headers
        .map((header, index) => {
          const cell = Array.isArray(row)
            ? row[index]
            : /** @type {Record<string, unknown>} */ (row)[header] ?? row[index];
          return `<td>${cell ?? ''}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  // Structure mirrors snippets/size-guide-measurement-table.liquid so one
  // set of styles covers both the Liquid and Storefront API render paths.
  return `
    <div class="size-guide__table-scroller" data-size-guide-scroller>
      <button
        type="button"
        class="size-guide__table-prev"
        data-size-guide-table-prev
        aria-label="Previous"
        hidden
      >
        <span class="svg-wrapper" aria-hidden="true">${CARET_LEFT_SVG}</span>
      </button>
      <div class="size-guide__table-wrap" data-size-guide-table-wrap>
        <table class="size-guide__table">
          <thead><tr>${headHtml}</tr></thead>
          <tbody>${bodyHtml}</tbody>
        </table>
      </div>
      <button
        type="button"
        class="size-guide__table-next"
        data-size-guide-table-next
        aria-label="${scrollLabel()}"
        hidden
      >
        <span class="svg-wrapper" aria-hidden="true">${CARET_RIGHT_SVG}</span>
      </button>
    </div>
  `;
}

/**
 * Converts Shopify Rich Text JSON AST string to HTML.
 * @param {string} jsonString
 * @returns {string}
 */
function parseRichTextJson(jsonString) {
  if (!jsonString) return '';
  const trimmed = jsonString.trim();
  if (!trimmed.startsWith('{')) return trimmed;

  try {
    const ast = JSON.parse(trimmed);
    return renderAstNode(ast);
  } catch {
    return '';
  }
}

/**
 * @param {any} node
 * @returns {string}
 */
function renderAstNode(node) {
  if (!node) return '';
  if (Array.isArray(node)) {
    return node.map(renderAstNode).join('');
  }
  if (node.type === 'text') {
    let text = node.value || '';
    if (node.bold) text = `<strong>${text}</strong>`;
    if (node.italic) text = `<em>${text}</em>`;
    if (node.underline) text = `<u>${text}</u>`;
    return text;
  }
  const childrenHtml = node.children ? node.children.map(renderAstNode).join('') : '';
  switch (node.type) {
    case 'root':
      return childrenHtml;
    case 'paragraph':
      return `<p>${childrenHtml}</p>`;
    case 'heading':
      const level = node.level || 2;
      return `<h${level}>${childrenHtml}</h${level}>`;
    case 'list':
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      return `<${tag}>${childrenHtml}</${tag}>`;
    case 'list-item':
      return `<li>${childrenHtml}</li>`;
    case 'link':
      return `<a href="${node.url || '#'}" target="${node.target || '_self'}">${childrenHtml}</a>`;
    default:
      return childrenHtml;
  }
}

/**
 * @param {MetaobjectField[]} fields
 * @param {string} fileKey
 * @param {string} textKey
 * @param {string} modifier
 */
function renderMediaBlock(fields, fileKey, textKey, modifier) {
  const fileField = getField(fields, fileKey);
  const media = getMediaFromReference(fileField?.reference);
  const richText = getFieldValue(fields, textKey);

  let html = '';

  if (media) {
    html += `<div class="size-guide__media size-guide__media--${modifier}">`;
    if (media.isVideo) {
      html += `<video class="size-guide__video" controls playsinline preload="metadata" src="${media.url}"></video>`;
    } else {
      html += `<img class="size-guide__image" loading="lazy" src="${media.url}" alt="">`;
    }
    html += '</div>';
  }

  if (richText) {
    const richHtml = parseRichTextJson(richText);
    if (richHtml) {
      html += `<div class="size-guide__richtext size-guide__richtext--${modifier} rte">${richHtml}</div>`;
    }
  }

  return html;
}

/**
 * @param {MetaobjectField[]} fields
 */
function renderSizeGuideContent(fields) {
  let measurementHtml = '';

  const measurementValue = getFieldValue(fields, 'measurement_table');
  if (measurementValue) {
    try {
      measurementHtml = renderMeasurementTable(JSON.parse(measurementValue));
    } catch {
      measurementHtml = '';
    }
  }

  return `
    <div class="size-guide-drawer__content" data-size-guide-drawer-content>
      ${measurementHtml}
      ${renderMediaBlock(fields, 'file_1_optional', 'free_text_below_image_1_optional', '1')}
      ${renderMediaBlock(fields, 'file_2_optional', 'free_text_below_image_2_optional', '2')}
      ${renderMediaBlock(fields, 'file_3_optional', 'free_text_below_image_3_optional', '3')}
    </div>
  `;
}

/**
 * @param {HTMLElement} drawerBody
 */
function hydrateLiquidTemplate(drawerBody) {
  const template = document.getElementById('size-guide-liquid-content');
  if (!(template instanceof HTMLTemplateElement)) return false;

  drawerBody.replaceChildren(template.content.cloneNode(true));
  return true;
}

/**
 * @param {Document | HTMLElement} [root]
 */
function hideAllTriggers(root = document) {
  root.querySelectorAll('.size-guide__trigger').forEach((trigger) => {
    if (trigger instanceof HTMLElement) {
      trigger.hidden = true;
      trigger.style.display = 'none';
    }
  });
}

/**
 * Check if the size guide drawer body contains meaningful size chart/media/text content.
 * @param {HTMLElement | null} drawerBody
 * @returns {boolean}
 */
function hasSizeGuideContent(drawerBody) {
  if (!drawerBody) return false;
  const content = drawerBody.querySelector('[data-size-guide-drawer-content]') || drawerBody;
  if (!content) return false;

  const hasTable = content.querySelector('.size-guide__table, table') !== null;
  const hasMedia = content.querySelector('.size-guide__image, .size-guide__video, img, video') !== null;
  const hasText = (content.querySelector('.size-guide__richtext, .rte')?.textContent.trim().length ?? 0) > 0;
  
  if (hasTable || hasMedia || hasText) return true;

  return content.children.length > 0 && content.textContent.trim().length > 0;
}

/**
 * @param {Document} [root]
 */
function revealPendingTriggers(root = document) {
  const dialog = document.querySelector('size-guide-dialog');
  const drawerBody = dialog?.querySelector('.size-guide-drawer__body');
  if (!hasSizeGuideContent(drawerBody)) {
    hideAllTriggers(root);
    return;
  }
  root.querySelectorAll('.size-guide__trigger').forEach((trigger) => {
    if (trigger instanceof HTMLElement) {
      trigger.hidden = false;
      trigger.style.removeProperty('display');
    }
  });
}

class SizeGuideDialog extends DialogComponent {
  /** @type {boolean} */
  #initialized = false;

  /** @type {string | null} */
  #loadedParentCode = null;

  /**
   * Parent code the in-flight (or most recently started) #initialize()
   * call is loading, set synchronously before its async Storefront API
   * fetch - lets resetForNewProduct() recognise "still loading this exact
   * same product's guide" as a same-product event (e.g. a Size switch)
   * rather than starting a second, redundant init.
   * @type {string | null}
   */
  #pendingParentCode = null;

  /**
   * Bumped every time #initialize() starts. A same-product `variant:update`
   * (e.g. a Size switch) fires on every selection, including while the
   * ORIGINAL #initialize() call from connectedCallback is still awaiting
   * its Storefront API fetch - without this guard, resetForNewProduct()
   * would start a second concurrent #initialize() call, and whichever of
   * the two finishes LAST wins, with no ordering guarantee. If that's the
   * stale one, it can call hideAllTriggers() and leave the trigger hidden
   * even though the guide is genuinely available, with nothing left to
   * correct it - matching the reported bug (works on first load, breaks
   * after switching, only a page refresh brings the link back). Each
   * #initialize() call captures its own generation and checks it again
   * after every `await`, so a call superseded by a newer one becomes a
   * no-op instead of overwriting state the newer call already set.
   * @type {number}
   */
  #initGeneration = 0;

  /** @type {HTMLElement | null} */
  #dragWrap = null;
  #startX = 0;
  #scrollLeft = 0;
  #isMouseDown = false;
  #isDragging = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleOpenClick);
    this.addEventListener('click', this.#handleChartScrollClick);
    this.addEventListener('click', this.#handleMoreClick);
    this.addEventListener('scroll', this.#handleChartScroll, true);
    this.addEventListener('mousedown', this.#handleMouseDown);
    document.addEventListener('mousemove', this.#handleMouseMove);
    document.addEventListener('mouseup', this.#handleMouseUp);
    // Images, videos and fonts change the guide's height after it opens.
    const body = this.querySelector('.size-guide-drawer__body');
    if (body instanceof HTMLElement && 'ResizeObserver' in window) {
      this.#bodyResizeObserver = new ResizeObserver(() => this.#syncMoreIndicator());
      this.#bodyResizeObserver.observe(body);
      new MutationObserver(() => {
        const content = body.querySelector('[data-size-guide-drawer-content]');
        if (content) this.#bodyResizeObserver?.observe(content);
        this.#syncMoreIndicator();
      }).observe(body, { childList: true });
      const content = body.querySelector('[data-size-guide-drawer-content]');
      if (content) this.#bodyResizeObserver.observe(content);
    }
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleOpenClick);
    this.removeEventListener('click', this.#handleChartScrollClick);
    this.removeEventListener('click', this.#handleMoreClick);
    this.removeEventListener('scroll', this.#handleChartScroll, true);
    this.#bodyResizeObserver?.disconnect();
    this.removeEventListener('mousedown', this.#handleMouseDown);
    document.removeEventListener('mousemove', this.#handleMouseMove);
    document.removeEventListener('mouseup', this.#handleMouseUp);
  }

  /**
   * Sync visibility of prev and next caret buttons based on scroll position.
   * @param {Event} [event]
   */
  #syncChartScrollControl = (event) => {
    const scrollers = this.querySelectorAll('[data-size-guide-scroller]');

    for (const scroller of scrollers) {
      const wrap = scroller.querySelector('[data-size-guide-table-wrap]');
      const prevBtn = scroller.querySelector('[data-size-guide-table-prev]');
      const nextBtn = scroller.querySelector('[data-size-guide-table-next]');
      if (!(wrap instanceof HTMLElement)) continue;
      if (event && event.target !== wrap) continue;

      const hasOverflow = wrap.scrollWidth > wrap.clientWidth;
      const canScrollLeft = wrap.scrollLeft > 1;
      const canScrollRight = wrap.scrollWidth - wrap.clientWidth - wrap.scrollLeft > 1;

      if (prevBtn instanceof HTMLElement) {
        prevBtn.toggleAttribute('hidden', !hasOverflow || !canScrollLeft);
      }
      if (nextBtn instanceof HTMLElement) {
        nextBtn.toggleAttribute('hidden', !hasOverflow || !canScrollRight);
      }
    }
  };

  #handleChartScroll = (/** @type {Event} */ event) => {
    if (event.target instanceof HTMLElement && event.target.hasAttribute('data-size-guide-table-wrap')) {
      this.#syncChartScrollControl(event);
    }
    if (event.target instanceof HTMLElement && event.target.classList.contains('size-guide-drawer__body')) {
      this.#syncMoreIndicator();
    }
  };

  /** @type {ResizeObserver | null} */
  #bodyResizeObserver = null;

  /**
   * "Scroll for more" at the bottom of the drawer while part of the guide is still below the fold
   * (the scrollbar is hidden, so nothing else hinted at it - Teamwork 41806966).
   */
  #syncMoreIndicator = () => {
    const body = this.querySelector('.size-guide-drawer__body');
    const more = this.querySelector('[data-size-guide-more]');
    if (!(body instanceof HTMLElement) || !(more instanceof HTMLElement)) return;
    const remaining = body.scrollHeight - body.clientHeight - body.scrollTop;
    more.hidden = remaining <= 24;
  };

  #handleMoreClick = (/** @type {Event} */ event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-size-guide-more]')) return;
    const body = this.querySelector('.size-guide-drawer__body');
    if (body instanceof HTMLElement) body.scrollBy({ top: body.clientHeight * 0.75, behavior: 'smooth' });
  };

  #handleChartScrollClick = (/** @type {Event} */ event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const nextBtn = target.closest('[data-size-guide-table-next]');
    if (nextBtn) {
      const wrap = nextBtn.parentElement?.querySelector('[data-size-guide-table-wrap]');
      if (wrap instanceof HTMLElement) {
        wrap.scrollBy({ left: CHART_SCROLL_STEP, behavior: 'smooth' });
      }
      return;
    }

    const prevBtn = target.closest('[data-size-guide-table-prev]');
    if (prevBtn) {
      const wrap = prevBtn.parentElement?.querySelector('[data-size-guide-table-wrap]');
      if (wrap instanceof HTMLElement) {
        wrap.scrollBy({ left: -CHART_SCROLL_STEP, behavior: 'smooth' });
      }
    }
  };

  #handleMouseDown = (/** @type {MouseEvent} */ event) => {
    if (event.button !== 0) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    const wrap = target.closest('[data-size-guide-table-wrap]');
    if (!(wrap instanceof HTMLElement)) return;

    if (wrap.scrollWidth <= wrap.clientWidth) return;

    this.#dragWrap = wrap;
    this.#isMouseDown = true;
    this.#isDragging = false;
    this.#startX = event.pageX - wrap.offsetLeft;
    this.#scrollLeft = wrap.scrollLeft;
  };

  #handleMouseMove = (/** @type {MouseEvent} */ event) => {
    if (!this.#isMouseDown || !this.#dragWrap) return;

    const x = event.pageX - this.#dragWrap.offsetLeft;
    const walk = x - this.#startX;

    if (!this.#isDragging && Math.abs(walk) > 3) {
      this.#isDragging = true;
      this.#dragWrap.classList.add('is-dragging');
    }

    if (this.#isDragging) {
      event.preventDefault();
      this.#dragWrap.scrollLeft = this.#scrollLeft - walk;
    }
  };

  #handleMouseUp = () => {
    if (this.#dragWrap) {
      this.#dragWrap.classList.remove('is-dragging');
    }
    this.#isMouseDown = false;
    this.#isDragging = false;
    this.#dragWrap = null;
  };

  async #initialize() {
    if (this.#initialized) return;

    const drawerBody = this.querySelector('.size-guide-drawer__body');
    if (!(drawerBody instanceof HTMLElement)) {
      hideAllTriggers();
      return;
    }

    const parentCode = this.#getParentCodeFromPage();
    this.#pendingParentCode = parentCode;
    const generation = ++this.#initGeneration;

    if (hydrateLiquidTemplate(drawerBody)) {
      if (hasSizeGuideContent(drawerBody)) {
        this.#initialized = true;
        this.#loadedParentCode = parentCode;
        revealPendingTriggers();
        this.#syncChartScrollControl();
      } else {
        hideAllTriggers();
        drawerBody.innerHTML = '';
      }
      return;
    }

    const config = getStorefrontConfig();

    if (!config || !parentCode) {
      hideAllTriggers();
      return;
    }

    try {
      const fields = await fetchSizeGuideByParentCode(config, parentCode);
      // A newer #initialize() call has started since this one's fetch was
      // issued (e.g. a same-product Size switch while this fetch was still
      // in flight) - its own result is what should apply, not this stale one.
      if (generation !== this.#initGeneration) return;

      if (!fields?.length) {
        hideAllTriggers();
        return;
      }

      drawerBody.innerHTML = renderSizeGuideContent(fields);
      if (hasSizeGuideContent(drawerBody)) {
        this.#initialized = true;
        this.#loadedParentCode = parentCode;
        revealPendingTriggers();
        this.#syncChartScrollControl();
      } else {
        hideAllTriggers();
        drawerBody.innerHTML = '';
      }
    } catch (error) {
      if (generation !== this.#initGeneration) return;
      console.error('Size guide lookup failed:', error);
      hideAllTriggers();
    }
  }

  showDialog() {
    super.showDialog();
    // Widths only resolve once the dialog is on screen, so the scroll
    // control cannot be measured until after it opens.
    requestAnimationFrame(() => {
      this.#syncChartScrollControl();
      this.#syncMoreIndicator();
    });
  }

  /**
   * Stitched colour swaps share one size guide per parent_identifier — skip refetch when unchanged.
   */
  resetForNewProduct() {
    const nextParentCode = this.#getParentCodeFromPage();
    const drawerBody = this.querySelector('.size-guide-drawer__body');

    if (this.#initialized && nextParentCode && nextParentCode === this.#loadedParentCode) {
      if (hasSizeGuideContent(drawerBody)) {
        revealPendingTriggers();
      } else {
        hideAllTriggers();
      }
      return;
    }

    // A same-product event (e.g. a Size switch, which fires variant:update
    // just like a colour swap does) landing while the very first
    // #initialize() call is still awaiting its Storefront API fetch is the
    // same product/guide, not a new one - let that in-flight call finish
    // and reveal/hide on its own rather than starting a second, redundant
    // (and previously racy) init for the exact same parent code.
    if (!this.#initialized && nextParentCode && nextParentCode === this.#pendingParentCode) {
      return;
    }

    this.#initialized = false;
    this.#loadedParentCode = null;
    this.#pendingParentCode = null;

    if (drawerBody instanceof HTMLElement) {
      drawerBody.innerHTML = '';
    }

    document.querySelectorAll('.size-guide__trigger').forEach((trigger) => {
      if (trigger instanceof HTMLElement) {
        trigger.hidden = true;
        trigger.style.display = 'none';
        trigger.classList.add('size-guide__trigger--pending');
      }
    });

    this.#initialize();
  }

  /**
   * @returns {string}
   */
  #getParentCodeFromPage() {
    return (
      document.querySelector('[data-size-guide-parent-code]')?.getAttribute('data-size-guide-parent-code')?.trim() ??
      ''
    );
  }

  /**
   * @param {Event} event
   */
  #handleOpenClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-size-guide-open]') : null;
    if (!(trigger instanceof HTMLButtonElement) || trigger.hidden) return;

    const drawerBody = this.querySelector('.size-guide-drawer__body');
    if (!hasSizeGuideContent(drawerBody)) {
      event.preventDefault();
      event.stopPropagation();
      hideAllTriggers();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.showDialog();
    this.querySelector('.size-guide-drawer__body')?.scrollTo({ top: 0, behavior: 'instant' });
    this.#syncMoreIndicator();
  };
}

if (!customElements.get('size-guide-dialog')) {
  customElements.define('size-guide-dialog', SizeGuideDialog);
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const dialog = document.querySelector('size-guide-dialog');
  if (dialog instanceof SizeGuideDialog) {
    dialog.resetForNewProduct();
  }
});
